import {type ApolloError} from '@apollo/client';
import {useCallback, useMemo} from 'react';

import {type MetricColumn, useMetricSearchQuery} from '../generated/graphql';
import {toMongo} from './filters';
import {type Filter} from './filterTypes';
import {type Key as RunKey} from './runTypes';

export type MetricSearchResults = {
  metrics: {
    key: string;
    types: string[];
    column: MetricColumn;
  }[];
  hasMore: boolean;
  nextCursor: string;
};

type UseMetricSearchProps = {
  projectName: string;
  entityName: string;
  filters?: Filter<RunKey>;

  /** NOTE: Backend currently does not guarantee a specific number of results per page. */
  pageSize: number;
  cursor?: string;
  /** RE2 Regex pattern to filter metrics by name. */
  metricNamePattern?: string;
  skip?: boolean;
  requiredRunKeys?: string[];
  runLimit?: number;
  runOrder?: string;
  /** Filter metrics to those whose `types` include any of these (backend `metricTypes`). */
  types?: string[];
};
export const useMetricSearch = ({
  projectName,
  entityName,
  filters,
  pageSize,
  cursor,
  metricNamePattern,
  skip,
  requiredRunKeys,
  runLimit,
  runOrder,
  types,
}: UseMetricSearchProps): {
  data: MetricSearchResults | null;
  loading: boolean;
  error: ApolloError | undefined;
  retry: () => void;
} => {
  const {data, loading, error, refetch} = useMetricSearchQuery({
    skip,
    variables: {
      projectName,
      entityName,
      filters: filters ? JSON.stringify(toMongo(filters)) : undefined,
      runLimit,
      runOrder,
      requiredRunKeys:
        requiredRunKeys != null && requiredRunKeys.length > 0
          ? requiredRunKeys
          : undefined,
      metricTypes: types != null && types.length > 0 ? types : undefined,
      keysPageSize: pageSize,
      cursor,
      metricNamePattern,
    },
    errorPolicy: 'all',
  });

  const metricSearchResults: MetricSearchResults | null = useMemo(() => {
    const metricSearch = data?.project?.runs?.metricSearch;
    if (!metricSearch) {
      return null;
    }

    const metrics = metricSearch.results.map(res => ({
      key: res.key,
      types: res.types,
      column: res.column,
    }));

    return {
      metrics,
      hasMore: metricSearch.hasMore,
      nextCursor: metricSearch.cursor,
    };
  }, [data]);

  const retry = useCallback(() => {
    refetch();
  }, [refetch]);

  return useMemo(
    () => ({data: metricSearchResults, loading, error, retry}),
    [metricSearchResults, loading, error, retry]
  );
};
